// Sends the meeting email from the shared mailbox through Microsoft Graph (spec §3.5), with the
// signed-in user's delegated Mail.Send.Shared token. Graph answers 202 with no body on success.
import { SHARED_MAILBOX } from "./config.js";

export const SEND_DENIED = `You don’t have send access to ${SHARED_MAILBOX.address} — ask an admin.`;
export const SEND_FAILED = "The email wasn’t sent — Microsoft 365 didn’t respond. Try again.";
export const SEND_UNCONFIRMED = `Microsoft 365 didn’t confirm the send — check Sent Items in ${SHARED_MAILBOX.address} before sending again.`;
export const SEND_RECONNECT = 'Your Microsoft 365 sign-in needs refreshing — use “Connect Microsoft 365”, then send again.';
const DENIED_CODES = new Set(["ErrorSendAsDenied", "ErrorAccessDenied", "AccessDenied"]);
const addr = (r) => ({ emailAddress: { address: r.address, ...(r.name ? { name: r.name } : {}) } });

export async function sendMeetingEmail({ token, subject, html, to, cc = [], replyTo = [], fetchImpl = (...a) => fetch(...a), timeoutMs = 20000 }) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res;
  try {
    res = await fetchImpl(`https://graph.microsoft.com/v1.0/users/${encodeURIComponent(SHARED_MAILBOX.graphId)}/sendMail`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        message: {
          subject,
          body: { contentType: "HTML", content: html },
          from: { emailAddress: { address: SHARED_MAILBOX.address } },
          toRecipients: to.map(addr), ccRecipients: cc.map(addr), replyTo: replyTo.map(addr),
        },
        saveToSentItems: true,
      }),
      signal: ctrl.signal,
    });
  } catch {
    // Timed out or the connection dropped: Graph may still have sent it.
    return { ok: false, kind: "unknown", error: SEND_UNCONFIRMED };
  } finally {
    clearTimeout(timer);
  }
  if (res.status === 202 || res.ok) return { ok: true };
  let code = "";
  try { code = (await res.json())?.error?.code || ""; } catch { /* no body */ }
  if (res.status === 403 || DENIED_CODES.has(code)) return { ok: false, kind: "denied", error: SEND_DENIED };
  if (res.status === 401) return { ok: false, kind: "reconnect", error: SEND_RECONNECT };
  return { ok: false, kind: "failed", error: SEND_FAILED };
}
